import type { ReactNode } from "react";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { IGeocodeResult } from "../../../api/mapController";

export interface GeoFilter {
	poi: IGeocodeResult | undefined;
	distanceMiles: number | string;
	distanceShape: "circle" | "square";
}

interface MapFiltersContextValue {
	filters: GeoFilter;
	filterChanged: <K extends keyof GeoFilter>(value: GeoFilter[K], name: K) => void;
}

const MapFiltersContext = createContext<MapFiltersContextValue | null>(null);

const DEFAULT_GEO_FILTER: GeoFilter = { poi: undefined, distanceMiles: 250, distanceShape: "square" };

export function geoFilterArea({ poi, distanceMiles, distanceShape }: GeoFilter) {
	const miles = Number(distanceMiles);
	return poi && miles > 0 ? { poi, miles, shape: distanceShape } : undefined;
}

export function MapFiltersProvider({ children }: { children: ReactNode }) {
	const [filters, setFilters] = useState(DEFAULT_GEO_FILTER);

	const filterChanged = useCallback<MapFiltersContextValue["filterChanged"]>(
		(value, name) => setFilters((prev) => (prev[name] === value ? prev : { ...prev, [name]: value })),
		[],
	);

	const value = useMemo(() => ({ filters, filterChanged }), [filters, filterChanged]);

	return <MapFiltersContext.Provider value={value}>{children}</MapFiltersContext.Provider>;
}

export function useMapFilters() {
	const context = useContext(MapFiltersContext);
	if (!context) throw new Error("useMapFilters must be used within a MapFiltersProvider");
	return context;
}
